import { pickBestLocationFields } from './geoUtils.js';
import { mergeUniqueStringArrays, pickProfileField } from './dataSafety.js';
import { pickBestUid } from './uidUtils.js';

export const fetchUserFromCloudByPhone = async (db, ensureAuth, phone) => {
  if (!db || !phone) return null;
  try {
    if (typeof ensureAuth === 'function') await ensureAuth();
    const res = await db.collection('users').where({ phone: String(phone) }).limit(1).get();
    if (!res?.data?.length) return null;
    const doc = res.data[0];
    return { ...doc, id: doc._id, _cloudId: doc._id };
  } catch (error) {
    console.warn('云库用户资料读取失败:', error);
    return null;
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const fetchUserFromCloudByPhoneWithRetry = async (
  db,
  ensureAuth,
  phone,
  retries = 3
) => {
  for (let attempt = 0; attempt < retries; attempt += 1) {
    const user = await fetchUserFromCloudByPhone(db, ensureAuth, phone);
    if (user) return user;
    if (attempt < retries - 1) await sleep(350 * (attempt + 1));
  }
  return null;
};

/** 云库资料优先合并到当前登录态（UID、关注、头像、封禁状态等跨设备一致） */
export const mergeCloudProfileIntoAppProfile = (appProfile, cloudUser) => {
  if (!appProfile || !cloudUser) return appProfile;
  const locationFields = pickBestLocationFields(cloudUser, appProfile);
  const isAdminProfile =
    appProfile?.role === 'super_admin' ||
    cloudUser?.role === 'super_admin' ||
    String(appProfile?.phone || cloudUser?.phone || '') === '18100136698';
  return {
    ...appProfile,
    id: cloudUser._cloudId || cloudUser._id || cloudUser.id || appProfile.id,
    uid: pickBestUid(appProfile, cloudUser, isAdminProfile),
    name: pickProfileField('name', appProfile, cloudUser),
    bio: pickProfileField('bio', appProfile, cloudUser),
    avatar: pickProfileField('avatar', appProfile, cloudUser),
    coverImg: pickProfileField('coverImg', appProfile, cloudUser),
    ...locationFields,
    role: cloudUser.role || appProfile.role,
    isVerified: cloudUser.isVerified ?? appProfile.isVerified,
    followingList: Array.isArray(cloudUser.followingList)
      ? cloudUser.followingList
      : appProfile.followingList,
    followers: cloudUser.followers ?? appProfile.followers,
    legacyUids: mergeUniqueStringArrays(cloudUser.legacyUids, appProfile.legacyUids),
    profileUpdatedAt: Math.max(
      Number(appProfile.profileUpdatedAt || 0),
      Number(cloudUser.profileUpdatedAt || cloudUser.updatedAt || 0)
    ),
    cloudUserId: cloudUser._cloudId || cloudUser._id || appProfile.cloudUserId,
    isBanned: cloudUser.isBanned ?? appProfile.isBanned ?? false,
    banDays: cloudUser.banDays ?? appProfile.banDays ?? 0,
    banUntil: cloudUser.banUntil ?? appProfile.banUntil ?? null,
    bannedAt: cloudUser.bannedAt ?? appProfile.bannedAt ?? null,
    bannedBy: cloudUser.bannedBy ?? appProfile.bannedBy ?? '',
    bannedByName: cloudUser.bannedByName ?? appProfile.bannedByName ?? ''
  };
};

export const extractModerationFields = (row) => ({
  role: row?.role,
  isBanned: !!row?.isBanned,
  banDays: row?.banDays ?? 0,
  banUntil: row?.banUntil ?? null,
  bannedAt: row?.bannedAt ?? null,
  bannedBy: row?.bannedBy ?? '',
  bannedByName: row?.bannedByName ?? '',
  adminUntil: row?.adminUntil ?? null
});

export const profileMatchesUserKey = (profile, userKey) => {
  if (!profile || userKey == null || userKey === '') return false;
  const key = String(userKey);
  const keys = [
    profile.id,
    profile.uid,
    profile.phone,
    profile.userId,
    profile._id,
    ...(Array.isArray(profile.legacyUids) ? profile.legacyUids : [])
  ]
    .filter(Boolean)
    .map(String);
  return keys.some((v) => v === key);
};

export const profileActorKeys = (profile) => {
  if (!profile) return [];
  return [
    ...new Set(
      [
        profile.uid,
        profile.phone,
        profile.id,
        ...(Array.isArray(profile.legacyUids) ? profile.legacyUids : [])
      ]
        .filter(Boolean)
        .map(String)
    )
  ];
};

export const primaryActorKey = (profile) =>
  String(profile?.uid || profile?.phone || profile?.id || '');
